/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { gql } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState, type FC, type FormEvent, type KeyboardEvent } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Activity,
  AlertCircle,
  Atom,
  BarChart3,
  CheckCircle2,
  Clock3,
  Download,
  FileSpreadsheet,
  Loader2,
  Plus,
  Send,
  Sparkles,
  Wrench,
  X,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../components/ui/chart";
import { useCustomer } from "../hooks/useCustomer";
import { useAuth } from "../hooks/useAuth";
import { useRunSelector } from "../hooks/useRunSelector";
import { formatRunOptionLabel } from "../hooks/runSelectionState";
import { apolloClient } from "../services/backend_gateway";
import {
  START_NUCLEUS_TURN,
  START_NUCLEUS_TURN_LEGACY,
  type StartNucleusTurnResult,
  type StartNucleusTurnVariables,
} from "../services/backend_gateway/mutations/start_nucleus_turn";
import { getNormalizedErrorMessage } from "../services/appsync/subscription-error-utils";
import { appSyncService } from "../services/appsync/service";
import { ChatThreadStatus, type ChatEvent } from "../services/appsync/types";
import { alpha, theme } from "../styles/theme/theme";
import {
  buildHiddenTurnPreview,
  buildTurnSummaries,
  buildTurnViewModel,
  type ArtifactDownload,
  type ChartArtifact,
  type CsvArtifact,
  type TableArtifact,
  limitVisibleTurns,
  mergeEvent,
  mergeEvents,
  type TurnArtifact,
  type TurnViewModel,
} from "./nucleus/chatTurnState";
import { runFakeChatTurn } from "./nucleus/fakeEventStream";

const NUCLEUS_COLOR = theme.colors.nucleus;
const NUCLEUS_FONT_FAMILY =
  '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

const POLL_DELAYS_MS = [150, 250, 350, 500, 750, 1000, 1200, 1500, 1800, 2000];
const MAX_BACKGROUND_SYNC_TOTAL_MS = 12 * 60 * 1000;
const MAX_BACKGROUND_SYNC_IDLE_MS = 3 * 60 * 1000;
const MAX_VISIBLE_PAGE_TURNS = 3;
const MAX_VISIBLE_DOCK_TURNS = 1;
const SUBMIT_DEDUPE_WINDOW_MS = 1500;
const STREAM_REVEAL_TICK_MS = 18;
const CSV_PREVIEW_ROW_LIMIT = 8;
const DEFAULT_SESSION_TITLE = "New chat";
const DEFAULT_THINKING_EFFORT: ThinkingEffort = "medium";
const SYNC_REFRESH_FAILURE_MESSAGE =
  "The turn was accepted, but the latest events could not be refreshed. Refresh the page if the answer does not appear.";

const GET_SIGNED_S3_URL = gql`
  query GetSignedS3Url($s3Url: String!, $expiresInSeconds: Int) {
    getSignedS3Url(s3Url: $s3Url, expiresInSeconds: $expiresInSeconds)
  }
`;
const SYNC_TIMEOUT_MESSAGE =
  "This turn is still running, but the latest response did not finish syncing. Refresh the page if the final answer does not appear.";

const THINKING_EFFORT_OPTIONS: Array<{ value: ThinkingEffort; label: string }> = [
  { value: "low", label: "Light" },
  { value: "medium", label: "Standard" },
  { value: "high", label: "Heavy" },
];

type PendingTurn = {
  id: string;
  text: string;
  createdAt: string;
  thinkingEffort: ThinkingEffort;
};

type QueuedTurn = PendingTurn;

type ChatSessionRecord = {
  threadId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

type ThinkingEffort = "low" | "medium" | "high";

type NucleusChatShellMode = "page" | "dock";

type NucleusChatViewProps = {
  shellMode?: NucleusChatShellMode;
  currentViewLabel?: string | null;
  onExpand?: () => void;
  onClose?: () => void;
};

const styles = {
  page: () => css`
    display: flex;
    min-height: 0;
    height: 100%;
    flex-direction: column;
    background: ${theme.colors.background};
    font-family: ${NUCLEUS_FONT_FAMILY};
  `,
  body: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    flex: 1;
    min-height: 0;
    justify-content: center;
    padding: ${shellMode === "page" ? "0.75rem" : "0.75rem 0.75rem 0.5rem"};
  `,
  chatShell: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    width: ${shellMode === "page" ? "min(100%, 68rem)" : "100%"};
    min-height: 0;
    flex: 1;
    flex-direction: column;
    gap: ${shellMode === "page" ? "0.875rem" : "0.7rem"};
  `,
  runToolbar: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.55rem;
    padding: ${shellMode === "page" ? "0.15rem 0" : "0.1rem 0"};
  `,
  runToolbarControls: css`
    display: flex;
    min-width: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.7rem;
  `,
  runToolbarLeft: css`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.625rem;
  `,
  runToolbarLabel: css`
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${theme.colors.mutedForeground};
  `,
  runSelector: css`
    width: min(100%, 17.5rem);
  `,
  sessionSelector: css`
    width: min(100%, 15rem);
  `,
  runTrigger: css`
    min-height: 2.05rem;
    border-radius: 0.75rem;
    border: 1px solid ${alpha(theme.colors.border, 0.95)};
    background: ${theme.colors.background};
    padding: 0.45rem 0.7rem;
    font-size: 0.8125rem;
    color: ${theme.colors.foreground};
    box-shadow: none !important;

    &:hover {
      background: ${alpha(theme.colors.gray50, 0.9)};
    }
  `,
  runContextText: css`
    margin: 0;
    margin-left: auto;
    font-size: 0.75rem;
    color: ${theme.colors.mutedForeground};
  `,
  brandBar: css`
    display: flex;
    width: 100%;
    justify-content: center;
    padding: 0.1rem 0 0;
  `,
  brandLockup: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    padding: 0.15rem 0;
  `,
  brandTitle: css`
    margin: 0;
    font-size: 1.35rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${theme.colors.foreground};
  `,
  betaBadge: css`
    display: inline-flex;
    align-items: center;
    border-radius: 0.35rem;
    background: ${alpha(NUCLEUS_COLOR, 0.16)};
    padding: 0.18rem 0.45rem;
    font-size: 0.68rem;
    font-weight: 700;
    color: ${NUCLEUS_COLOR};
  `,
  toolbarButton: css`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-height: 2.05rem;
    border: 1px solid ${alpha(theme.colors.border, 0.95)};
    border-radius: 0.8rem;
    background: ${theme.colors.background};
    padding: 0.45rem 0.7rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: ${theme.colors.foreground};
    cursor: pointer;

    &:hover {
      background: ${alpha(theme.colors.gray50, 0.9)};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  `,
  topCard: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    border: 1px solid ${alpha(theme.colors.border, 0.9)};
    border-radius: ${shellMode === "page" ? "1.25rem" : "1rem"};
    background: ${alpha(theme.colors.background, 0.92)};
    box-shadow: ${theme.shadows.sm};
    padding: ${shellMode === "page" ? "1rem 1.125rem" : "0.85rem 0.95rem"};
    backdrop-filter: blur(10px);
  `,
  topCardInfo: css`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.25rem;
  `,
  topEyebrow: css`
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${NUCLEUS_COLOR};
  `,
  topTitle: css`
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${theme.colors.foreground};
  `,
  topSubtitle: css`
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: ${theme.colors.mutedForeground};
  `,
  threadDetails: css`
    margin-top: 0.25rem;

    summary {
      cursor: pointer;
      font-size: 0.75rem;
      color: ${theme.colors.mutedForeground};
    }
  `,
  threadCode: css`
    margin: 0.5rem 0 0;
    border-radius: 0.875rem;
    background: ${theme.colors.gray50};
    padding: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.6875rem;
    line-height: 1.45;
    color: ${theme.colors.mutedForeground};
  `,
  badges: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  `,
  modeBadge: css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    border-radius: 9999px;
    border: 1px solid ${alpha(NUCLEUS_COLOR, 0.25)};
    background: ${alpha(NUCLEUS_COLOR, 0.1)};
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${NUCLEUS_COLOR};
  `,
  runBadge: css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    border-radius: 9999px;
    border: 1px solid ${alpha(theme.colors.border, 0.9)};
    background: ${theme.colors.background};
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${theme.colors.foreground};
  `,
  scopeBadge: css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    border-radius: 9999px;
    border: 1px solid ${alpha(theme.colors.blue500, 0.18)};
    background: ${alpha(theme.colors.blue50, 0.9)};
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${theme.colors.blue700};
  `,
  conversationPanel: css`
    display: flex;
    position: relative;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid ${alpha(theme.colors.border, 0.9)};
    border-radius: 1.5rem;
    background: ${alpha(theme.colors.background, 0.96)};
    box-shadow: ${theme.shadows.md};
    backdrop-filter: blur(12px);
  `,
  conversationPanelHero: (shellMode: NucleusChatShellMode) => css`
    flex: 1;
    min-height: ${shellMode === "page" ? "32rem" : "24rem"};
    border-color: ${alpha(theme.colors.border, 0.85)};
    background: ${theme.colors.background};
    box-shadow: ${theme.shadows.sm};
  `,
  transcriptBody: css`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    gap: 0.9rem;
    overflow-y: auto;
    padding: 1rem 1rem 0.9rem;
    scroll-behavior: smooth;

    @media (max-width: 768px) {
      padding: 0.9rem 0.85rem 0.85rem;
    }
  `,
  transcriptBodyHero: css`
    flex: 1;
    justify-content: center;
    padding: 1rem 1rem 0.4rem;
  `,
  emptyState: css`
    display: flex;
    position: relative;
    width: 100%;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    padding: 0;
    text-align: center;
    color: ${theme.colors.mutedForeground};
  `,
  heroPanel: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    position: relative;
    width: ${shellMode === "page" ? "min(100%, 32rem)" : "min(100%, 22rem)"};
    flex-direction: column;
    align-items: center;
    gap: ${shellMode === "page" ? "0.95rem" : "0.75rem"};
    padding: ${shellMode === "page" ? "1rem 1.25rem" : "0.35rem 0.25rem"};
    text-align: center;

    @media (max-width: 768px) {
      width: min(100%, 28rem);
      padding: 0.75rem 0.65rem;
    }
  `,
  heroIcon: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${NUCLEUS_COLOR};
    animation: nucleus-hero-spin 24s linear infinite;

    @keyframes nucleus-hero-spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `,
  heroSignal: css`
    position: relative;
    display: grid;
    width: 3.75rem;
    height: 3.75rem;
    place-items: center;
    margin-bottom: 0.05rem;
  `,
  heroSignalCore: css`
    display: grid;
    width: 3.25rem;
    height: 3.25rem;
    place-items: center;
    border: 1px solid ${alpha(NUCLEUS_COLOR, 0.14)};
    border-radius: 1rem;
    background: ${alpha(NUCLEUS_COLOR, 0.05)};
  `,
  heroTitle: css`
    margin: 0;
    font-size: 1.7rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${theme.colors.foreground};
  `,
  heroSubtitle: css`
    margin: 0;
    max-width: 31rem;
    font-size: 0.92rem;
    line-height: 1.55;
    color: ${theme.colors.mutedForeground};
  `,
  heroGreeting: css`
    margin: 0;
    border: 1px solid ${alpha(NUCLEUS_COLOR, 0.14)};
    border-radius: 9999px;
    background: ${theme.colors.background};
    padding: 0.34rem 0.72rem;
    font-size: 0.78rem;
    font-weight: 650;
    letter-spacing: 0.01em;
    color: ${NUCLEUS_COLOR};
  `,
  typingIndicator: css`
    display: inline-flex;
    align-items: baseline;
    margin-left: 0.18em;
  `,
  typingCursor: css`
    display: inline-block;
    width: 0.48em;
    height: 1em;
    margin-left: 0.08em;
    border-radius: 0.08em;
    background: ${NUCLEUS_COLOR};
    transform: translateY(0.12em);
    animation: nucleus-cursor-blink 1.1s steps(1, end) infinite;

    @keyframes nucleus-cursor-blink {
      0%,
      49% {
        opacity: 1;
      }
      50%,
      100% {
        opacity: 0.15;
      }
    }
  `,
  turnGroup: css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    animation: rise-in 0.22s ease-out;

    @keyframes rise-in {
      from {
        opacity: 0;
        transform: translateY(6px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
  userRow: css`
    display: flex;
    justify-content: flex-end;
  `,
  userBubble: css`
    max-width: min(38rem, 84%);
    border-radius: 1.1rem 1.1rem 0.4rem 1.1rem;
    border: 1px solid ${alpha(NUCLEUS_COLOR, 0.18)};
    background: ${alpha(NUCLEUS_COLOR, 0.07)};
    padding: 0.85rem 1rem;
  `,
  userMeta: css`
    margin: 0 0 0.25rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${alpha(theme.colors.foreground, 0.55)};
  `,
  userText: css`
    margin: 0;
    white-space: pre-wrap;
    font-size: 0.9rem;
    line-height: 1.55;
    color: ${theme.colors.foreground};
  `,
  assistantCard: (tone: "working" | "complete" | "error" | "info") => css`
    display: flex;
    position: relative;
    overflow: hidden;
    flex-direction: column;
    gap: 0.7rem;
    border-radius: 1rem;
    border: 1px solid
      ${tone === "error"
        ? theme.colors.red200
        : tone === "working"
          ? alpha(NUCLEUS_COLOR, 0.18)
          : alpha(theme.colors.border, 0.92)};
    background: ${theme.colors.background};
    padding: 0.85rem 0.95rem;
    box-shadow: ${tone === "working" ? theme.shadows.xs : "none"};

    ${tone === "working"
      ? `
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent 0%, ${alpha(NUCLEUS_COLOR, 0.08)} 50%, transparent 100%);
        transform: translateX(-100%);
        animation: active-card-sheen 2.4s ease-in-out infinite;
        pointer-events: none;
      }

      @keyframes active-card-sheen {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }
    `
      : ""}
  `,
  assistantHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  `,
  assistantMeta: css`
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${alpha(theme.colors.foreground, 0.55)};
  `,
  statusChip: (tone: "working" | "complete" | "error" | "info") => css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    border-radius: 9999px;
    padding: 0.275rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 600;
    color:
      ${tone === "error"
        ? theme.colors.red700
        : tone === "complete"
          ? theme.colors.green700
          : tone === "working"
            ? NUCLEUS_COLOR
            : theme.colors.mutedForeground};
    background:
      ${tone === "error"
        ? theme.colors.red50
        : tone === "complete"
          ? theme.colors.green50
          : tone === "working"
            ? alpha(NUCLEUS_COLOR, 0.1)
            : theme.colors.gray50};
    border: 1px solid
      ${tone === "error"
        ? theme.colors.red200
        : tone === "complete"
          ? alpha(theme.colors.green500, 0.3)
          : tone === "working"
            ? alpha(NUCLEUS_COLOR, 0.2)
            : theme.colors.border};
  `,
  liveStatus: (isRunning: boolean) => css`
    display: flex;
    position: relative;
    overflow: hidden;
    align-items: flex-start;
    gap: 0.625rem;
    border-radius: 0.85rem;
    background: ${alpha(theme.colors.gray50, 0.72)};
    padding: 0.65rem 0.75rem;

    ${isRunning
      ? `
      &::after {
        content: "";
        position: absolute;
        left: -30%;
        right: -30%;
        bottom: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, ${NUCLEUS_COLOR} 50%, transparent 100%);
        animation: live-status-scan 1.4s linear infinite;
      }

      @keyframes live-status-scan {
        0% {
          transform: translateX(-30%);
        }
        100% {
          transform: translateX(30%);
        }
      }
    `
      : ""}
  `,
  liveStatusTextWrap: css`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.16rem;
  `,
  liveStatusEyebrow: css`
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${theme.colors.mutedForeground};
  `,
  liveStatusText: css`
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.45;
    font-weight: 500;
    color: ${theme.colors.foreground};
  `,
  liveStatusDetail: css`
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.45;
    color: ${theme.colors.mutedForeground};
  `,
  markdown: css`
    color: ${theme.colors.foreground};
    font-size: 0.92rem;
    line-height: 1.7;
    font-weight: 400;

    > *:first-of-type {
      margin-top: 0;
    }

    > *:last-child {
      margin-bottom: 0;
    }

    p,
    ul,
    ol,
    blockquote,
    pre,
    table {
      margin: 0 0 0.85rem;
    }

    h1,
    h2,
    h3,
    h4 {
      margin: 0 0 0.65rem;
      color: ${theme.colors.foreground};
      line-height: 1.25;
    }

    h1 {
      font-size: 1.1rem;
    }

    h2 {
      font-size: 1rem;
    }

    h3,
    h4 {
      font-size: 0.92rem;
    }

    ul,
    ol {
      padding-left: 1.1rem;
    }

    li + li {
      margin-top: 0.25rem;
    }

    strong {
      color: ${theme.colors.foreground};
      font-weight: 600;
    }

    a {
      color: ${NUCLEUS_COLOR};
      text-decoration: none;
    }

    a:hover {
      text-decoration: underline;
    }

    code {
      border-radius: 0.45rem;
      background: ${theme.colors.gray100};
      padding: 0.12rem 0.35rem;
      font-size: 0.82em;
    }

    pre {
      overflow-x: auto;
      border-radius: 0.95rem;
      background: ${theme.colors.gray900};
      padding: 0.9rem 1rem;
      color: ${theme.colors.white};
    }

    pre code {
      background: transparent;
      padding: 0;
      color: inherit;
    }

    blockquote {
      border-left: 3px solid ${alpha(NUCLEUS_COLOR, 0.35)};
      padding-left: 0.85rem;
      color: ${theme.colors.mutedForeground};
    }
  `,
  traceSection: css`
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  `,
  traceSectionLabel: css`
    margin: 0;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${theme.colors.mutedForeground};
  `,
  thinkingList: css`
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  `,
  thinkingBlock: css`
    display: flex;
    gap: 0.55rem;
    align-items: flex-start;
    border-left: 2px solid ${alpha(NUCLEUS_COLOR, 0.22)};
    padding-left: 0.75rem;
    animation: thinking-in 0.18s ease-out;

    @keyframes thinking-in {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
  thinkingText: css`
    margin: 0;
    white-space: pre-wrap;
    font-size: 0.84rem;
    line-height: 1.6;
    color: ${theme.colors.mutedForeground};
  `,
  toolTraceList: css`
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  `,
  toolTraceRow: (state: "working" | "complete" | "error") => css`
    display: flex;
    position: relative;
    overflow: hidden;
    flex-direction: column;
    gap: 0.45rem;
    border-radius: 0.9rem;
    border: 1px solid ${alpha(theme.colors.border, 0.9)};
    background: ${theme.colors.gray50};
    padding: 0.7rem 0.8rem;
    transition:
      border-color 0.16s ease,
      box-shadow 0.16s ease,
      transform 0.16s ease;

    ${state === "working"
      ? `
      border-color: ${alpha(NUCLEUS_COLOR, 0.26)};
      box-shadow: inset 0 0 0 1px ${alpha(NUCLEUS_COLOR, 0.08)};

      &::after {
        content: "";
        position: absolute;
        inset: auto 0 0 0;
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, ${NUCLEUS_COLOR} 50%, transparent 100%);
        animation: tool-progress-scan 1.2s linear infinite;
      }

      @keyframes tool-progress-scan {
        0% {
          transform: translateX(-40%);
        }
        100% {
          transform: translateX(40%);
        }
      }
    `
      : ""}
  `,
  toolTraceHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  `,
  toolTraceTitleWrap: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  `,
  toolTraceTitle: css`
    margin: 0;
    font-size: 0.84rem;
    font-weight: 500;
    color: ${theme.colors.foreground};
  `,
  toolTraceState: (state: "working" | "complete" | "error") => css`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border-radius: 9999px;
    padding: 0.22rem 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
    color:
      ${state === "error"
        ? theme.colors.red700
        : state === "complete"
          ? theme.colors.green700
          : NUCLEUS_COLOR};
    background:
      ${state === "error"
        ? theme.colors.red50
        : state === "complete"
          ? theme.colors.green50
          : alpha(NUCLEUS_COLOR, 0.1)};
  `,
  placeholderAnswer: css`
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    color: ${theme.colors.mutedForeground};
    font-size: 0.875rem;
    animation: soft-pulse 1.4s ease-in-out infinite;

    @keyframes soft-pulse {
      0%,
      100% {
        opacity: 0.72;
      }
      50% {
        opacity: 1;
      }
    }
  `,
  streamingAnswer: css`
    margin: 0;
    white-space: pre-wrap;
    font-size: 0.9rem;
    line-height: 1.65;
    color: ${theme.colors.foreground};
  `,
  pulseDot: css`
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 9999px;
    background: ${NUCLEUS_COLOR};
    box-shadow: 0 0 0 0 ${alpha(NUCLEUS_COLOR, 0.45)};
    animation: pulse 1.3s infinite;

    @keyframes pulse {
      0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 ${alpha(NUCLEUS_COLOR, 0.35)};
      }
      70% {
        transform: scale(1);
        box-shadow: 0 0 0 8px ${alpha(NUCLEUS_COLOR, 0)};
      }
      100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 ${alpha(NUCLEUS_COLOR, 0)};
      }
    }
  `,
  spinningIcon: css`
    animation: nucleus-spin 1s linear infinite;

    @keyframes nucleus-spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `,
  workDetails: css`
    border-top: 1px solid ${alpha(theme.colors.border, 0.95)};
    padding-top: 0.85rem;

    summary {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      cursor: pointer;
      list-style: none;
      font-size: 0.8125rem;
      font-weight: 600;
      color: ${theme.colors.mutedForeground};
    }

    summary::-webkit-details-marker {
      display: none;
    }
  `,
  workList: css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.85rem;
  `,
  workItem: css`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.65rem;
    align-items: start;
    border-radius: 0.95rem;
    background: ${theme.colors.gray50};
    padding: 0.75rem 0.8rem;
  `,
  workIconWrap: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.6rem;
    height: 1.6rem;
    border-radius: 9999px;
    background: ${theme.colors.background};
    color: ${theme.colors.gray600};
  `,
  workTextWrap: css`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.15rem;
  `,
  workTitle: css`
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.colors.foreground};
  `,
  workSubtitle: css`
    margin: 0;
    font-size: 0.75rem;
    line-height: 1.45;
    color: ${theme.colors.mutedForeground};
  `,
  workInnerDetails: css`
    margin-top: 0.35rem;

    summary {
      cursor: pointer;
      font-size: 0.75rem;
      color: ${theme.colors.mutedForeground};
    }
  `,
  workCode: css`
    margin: 0.5rem 0 0;
    border-radius: 0.85rem;
    background: ${theme.colors.background};
    padding: 0.7rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.72rem;
    line-height: 1.55;
    color: ${theme.colors.mutedForeground};
  `,
  composer: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    border-top: 1px solid ${alpha(theme.colors.border, 0.9)};
    padding: ${shellMode === "page" ? "1rem 1.15rem 1.15rem" : "0.9rem 1rem 1rem"};
  `,
  composerHero: css`
    gap: 0.65rem;
    border-top: 1px solid ${alpha(theme.colors.border, 0.9)};
    padding: 0.85rem 1.15rem 1.05rem;
  `,
  composerRow: css`
    display: flex;
    gap: 0.75rem;
    align-items: flex-end;
  `,
  composerControls: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.6rem;
  `,
  composerControlLabel: css`
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${theme.colors.mutedForeground};
  `,
  effortSelector: css`
    width: 8.75rem;
  `,
  queueList: css`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  `,
  queueChip: css`
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    max-width: min(100%, 24rem);
    border-radius: 9999px;
    border: 1px solid ${alpha(theme.colors.border, 0.95)};
    background: ${alpha(theme.colors.gray50, 0.95)};
    padding: 0.42rem 0.65rem;
    font-size: 0.75rem;
    color: ${theme.colors.foreground};
  `,
  queueChipText: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  queueRemoveButton: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: ${theme.colors.mutedForeground};
    cursor: pointer;
    padding: 0;

    &:hover {
      color: ${theme.colors.foreground};
    }
  `,
  input: css`
    flex: 1;
    min-height: 3.65rem;
    max-height: 12rem;
    resize: vertical;
    border-radius: 1rem;
    border: 1px solid ${theme.colors.border};
    background: ${theme.colors.background};
    padding: 0.95rem 1rem;
    font-size: 0.9rem;
    color: ${theme.colors.foreground};
    outline: none;
    font-family: inherit;
    transition:
      border-color 0.16s ease,
      box-shadow 0.16s ease;

    &::placeholder {
      color: ${alpha(theme.colors.mutedForeground, 0.75)};
    }

    &:focus {
      border-color: ${NUCLEUS_COLOR};
      box-shadow: 0 0 0 3px ${alpha(NUCLEUS_COLOR, 0.12)};
    }
  `,
  sendButton: (disabled: boolean) => css`
    display: inline-flex;
    width: 3.3rem;
    height: 3.3rem;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 1rem;
    background: ${disabled ? theme.colors.gray200 : NUCLEUS_COLOR};
    color: ${disabled ? theme.colors.mutedForeground : theme.colors.background};
    box-shadow: ${disabled ? "none" : theme.shadows.sm};
    transition:
      transform 0.16s ease,
      box-shadow 0.16s ease,
      background 0.16s ease;
    cursor: ${disabled ? "not-allowed" : "pointer"};

    &:hover {
      transform: ${disabled ? "none" : "translateY(-1px)"};
      box-shadow: ${disabled ? "none" : theme.shadows.md};
    }
  `,
  footerStatus: css`
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.45;
    color: ${theme.colors.mutedForeground};
  `,
  artifactList: css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  `,
  artifactShelf: css`
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    border-radius: 1rem;
    border: 1px solid ${alpha(NUCLEUS_COLOR, 0.18)};
    background: linear-gradient(180deg, ${alpha(NUCLEUS_COLOR, 0.055)}, ${alpha(theme.colors.background, 0.96)});
    padding: 0.7rem 0.8rem;
  `,
  artifactShelfHeader: css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
  `,
  artifactShelfTitle: css`
    margin: 0;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${theme.colors.foreground};
  `,
  artifactShelfSubtitle: css`
    margin: 0;
    font-size: 0.74rem;
    color: ${theme.colors.mutedForeground};
  `,
  generatedFileList: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
    gap: 0.55rem;
  `,
  generatedFileCard: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border-radius: 0.8rem;
    border: 1px solid ${alpha(theme.colors.border, 0.78)};
    background: ${alpha(theme.colors.background, 0.94)};
    padding: 0.62rem 0.7rem;
  `,
  generatedFileText: css`
    min-width: 0;
  `,
  generatedFileTitle: css`
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.82rem;
    font-weight: 700;
    color: ${theme.colors.foreground};
  `,
  generatedFileMeta: css`
    margin: 0.12rem 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.72rem;
    color: ${theme.colors.mutedForeground};
  `,
  generatedFileActions: css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.4rem;
  `,
  artifactCard: css`
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    border-radius: 1rem;
    border: 1px solid ${alpha(theme.colors.border, 0.95)};
    background: ${alpha(theme.colors.gray50, 0.72)};
    padding: 0.85rem 0.9rem;
  `,
  artifactHeader: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.8rem;
  `,
  artifactTitleWrap: css`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.15rem;
  `,
  artifactTitle: css`
    margin: 0;
    font-size: 0.86rem;
    font-weight: 600;
    color: ${theme.colors.foreground};
  `,
  artifactSubtitle: css`
    margin: 0;
    font-size: 0.76rem;
    line-height: 1.45;
    color: ${theme.colors.mutedForeground};
  `,
  artifactAction: css`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border-radius: 9999px;
    border: 1px solid ${alpha(NUCLEUS_COLOR, 0.2)};
    background: ${alpha(NUCLEUS_COLOR, 0.08)};
    padding: 0.38rem 0.7rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${NUCLEUS_COLOR};
    cursor: pointer;

    &:hover {
      background: ${alpha(NUCLEUS_COLOR, 0.14)};
    }
  `,
  artifactMetaRow: css`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  `,
  artifactMetaChip: css`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border-radius: 9999px;
    background: ${theme.colors.background};
    padding: 0.3rem 0.55rem;
    font-size: 0.72rem;
    font-weight: 500;
    color: ${theme.colors.mutedForeground};
  `,
  artifactTableWrap: css`
    overflow: auto;
    max-height: 18rem;
    border: 1px solid ${alpha(theme.colors.border, 0.88)};
    border-radius: 0.85rem;
    background: ${theme.colors.background};
  `,
  artifactTable: css`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.75rem;

    th,
    td {
      border-bottom: 1px solid ${alpha(theme.colors.border, 0.72)};
      padding: 0.48rem 0.58rem;
      text-align: left;
      white-space: nowrap;
    }

    th {
      position: sticky;
      top: 0;
      z-index: 1;
      background: ${theme.colors.gray50};
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${theme.colors.mutedForeground};
    }

    tr:last-of-type td {
      border-bottom: none;
    }
  `,
  artifactTableRow: (isUserEditedRow: boolean) => css`
    ${isUserEditedRow
      ? `
        background: ${alpha(theme.colors.green500, 0.08)};

        td {
          color: ${theme.colors.foreground};
          font-weight: 600;
        }
      `
      : ""}
  `,
  chartFrame: css`
    height: 18rem;
    border-radius: 0.95rem;
    background: ${theme.colors.background};
    padding: 0.5rem 0.25rem 0.15rem;
  `,
  infoBanner: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    width: ${shellMode === "page" ? "min(100%, 68rem)" : "100%"};
    margin: ${shellMode === "page" ? "0 auto" : "0"};
    border-radius: 1rem;
    border: 1px solid ${alpha(NUCLEUS_COLOR, 0.25)};
    background: ${alpha(NUCLEUS_COLOR, 0.1)};
    padding: 0.9rem 1rem;
  `,
  infoText: css`
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: ${theme.colors.foreground};
  `,
  errorBanner: (shellMode: NucleusChatShellMode) => css`
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    width: ${shellMode === "page" ? "min(100%, 68rem)" : "100%"};
    margin: ${shellMode === "page" ? "0 auto" : "0"};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.red200};
    background: ${theme.colors.red50};
    padding: 0.9rem 1rem;
  `,
  errorText: css`
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: ${theme.colors.red700};
  `,
  developerDetails: css`
    border-radius: 1rem;
    border: 1px solid ${alpha(theme.colors.border, 0.92)};
    background: ${alpha(theme.colors.background, 0.92)};
    padding: 0.9rem 1rem;

    summary {
      cursor: pointer;
      font-size: 0.8rem;
      font-weight: 600;
      color: ${theme.colors.mutedForeground};
    }
  `,
  developerList: css`
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-top: 0.9rem;
  `,
  developerItem: css`
    border-radius: 0.85rem;
    border: 1px solid ${theme.colors.border};
    background: ${theme.colors.background};
    padding: 0.7rem 0.8rem;
  `,
  developerType: css`
    margin: 0 0 0.2rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${NUCLEUS_COLOR};
  `,
  developerText: css`
    margin: 0;
    font-size: 0.75rem;
    line-height: 1.45;
    color: ${theme.colors.foreground};
  `,
  developerInnerDetails: css`
    margin-top: 0.45rem;

    summary {
      cursor: pointer;
      font-size: 0.75rem;
      color: ${theme.colors.mutedForeground};
    }
  `,
  developerCode: css`
    margin: 0.45rem 0 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.72rem;
    line-height: 1.5;
    color: ${theme.colors.mutedForeground};
  `,
  historyDetails: css`
    border-radius: 0.95rem;
    border: 1px solid ${alpha(theme.colors.border, 0.9)};
    background: ${alpha(theme.colors.gray50, 0.72)};
    padding: 0.8rem 0.9rem;

    summary {
      cursor: pointer;
      font-size: 0.8rem;
      font-weight: 600;
      color: ${theme.colors.mutedForeground};
    }
  `,
  historyList: css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.75rem;
  `,
  historyItem: css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    border-radius: 0.8rem;
    background: ${theme.colors.background};
    padding: 0.65rem 0.75rem;
  `,
  historyItemHeader: css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
  `,
  historyItemText: css`
    margin: 0;
    font-size: 0.8rem;
    line-height: 1.45;
    color: ${theme.colors.foreground};
  `,
  historyItemMeta: css`
    flex-shrink: 0;
    margin: 0;
    font-size: 0.72rem;
    color: ${theme.colors.mutedForeground};
  `,
  historyItemAnswer: css`
    max-height: min(18rem, 42vh);
    overflow-y: auto;
    border-top: 1px solid ${alpha(theme.colors.border, 0.72)};
    padding-top: 0.55rem;
    font-size: 0.78rem;
    line-height: 1.5;
    color: ${theme.colors.mutedForeground};

    p {
      margin: 0 0 0.45rem;
    }

    p:last-child {
      margin-bottom: 0;
    }

    strong {
      color: ${theme.colors.foreground};
      font-weight: 650;
    }
  `,
};

const sanitizeThreadScopePart = (value: string) => {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return normalized.slice(0, 24) || "anonymous";
};

const buildThreadId = (runId: string, userScope: string) =>
  `nucleus-chat-${userScope}-${sanitizeThreadScopePart(runId)}-${Math.random().toString(36).slice(2, 10)}`;

const buildChatSessionsStorageKey = (runId: string, userScope: string) =>
  `nucleus-chat-sessions:${userScope}:${runId}`;

const buildActiveChatSessionStorageKey = (runId: string, userScope: string) =>
  `nucleus-active-chat:${userScope}:${runId}`;

const buildThinkingEffortStorageKey = (userScope: string) =>
  `nucleus-thinking-effort:${userScope}`;

const sortChatSessions = (sessions: ChatSessionRecord[]) =>
  [...sessions].sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());

const readStoredChatSessions = (runId: string, userScope: string): ChatSessionRecord[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const raw = window.localStorage.getItem(buildChatSessionsStorageKey(runId, userScope));
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return sortChatSessions(
      parsed.filter((entry): entry is ChatSessionRecord => {
        if (!entry || typeof entry !== "object") {
          return false;
        }
        const record = entry as Partial<ChatSessionRecord>;
        return (
          typeof record.threadId === "string" &&
          typeof record.title === "string" &&
          typeof record.createdAt === "string" &&
          typeof record.updatedAt === "string"
        );
      })
    );
  } catch {
    return [];
  }
};

const writeStoredChatSessions = (runId: string, userScope: string, sessions: ChatSessionRecord[]) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    buildChatSessionsStorageKey(runId, userScope),
    JSON.stringify(sortChatSessions(sessions))
  );
};

const readActiveChatSessionThreadId = (runId: string, userScope: string) => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage.getItem(buildActiveChatSessionStorageKey(runId, userScope));
};

const writeActiveChatSessionThreadId = (runId: string, userScope: string, threadId: string) => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(buildActiveChatSessionStorageKey(runId, userScope), threadId);
};

const createChatSessionRecord = (runId: string, userScope: string): ChatSessionRecord => {
  const now = new Date().toISOString();
  return {
    threadId: buildThreadId(runId, userScope),
    title: DEFAULT_SESSION_TITLE,
    createdAt: now,
    updatedAt: now,
  };
};

const ensureChatSessions = (runId: string, userScope: string) => {
  const existingSessions = readStoredChatSessions(runId, userScope);
  if (existingSessions.length === 0) {
    const initialSession = createChatSessionRecord(runId, userScope);
    writeStoredChatSessions(runId, userScope, [initialSession]);
    writeActiveChatSessionThreadId(runId, userScope, initialSession.threadId);
    return {
      sessions: [initialSession],
      activeThreadId: initialSession.threadId,
    };
  }

  const activeThreadId = readActiveChatSessionThreadId(runId, userScope);
  const selectedThreadId =
    activeThreadId && existingSessions.some((session) => session.threadId === activeThreadId)
      ? activeThreadId
      : existingSessions[0].threadId;

  writeActiveChatSessionThreadId(runId, userScope, selectedThreadId);
  writeStoredChatSessions(runId, userScope, existingSessions);
  return {
    sessions: existingSessions,
    activeThreadId: selectedThreadId,
  };
};

const createStoredChatSession = (runId: string, userScope: string) => {
  const nextSession = createChatSessionRecord(runId, userScope);
  const nextSessions = sortChatSessions([nextSession, ...readStoredChatSessions(runId, userScope)]);
  writeStoredChatSessions(runId, userScope, nextSessions);
  writeActiveChatSessionThreadId(runId, userScope, nextSession.threadId);
  return {
    sessions: nextSessions,
    session: nextSession,
  };
};

const updateStoredChatSession = (
  runId: string,
  userScope: string,
  threadId: string,
  updater: (session: ChatSessionRecord) => ChatSessionRecord
) => {
  const sessions = readStoredChatSessions(runId, userScope);
  const nextSessions = sortChatSessions(
    sessions.map((session) => (session.threadId === threadId ? updater(session) : session))
  );
  writeStoredChatSessions(runId, userScope, nextSessions);
  return nextSessions;
};

const buildChatSessionTitle = (text: string) => {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return DEFAULT_SESSION_TITLE;
  }
  if (normalized.length <= 48) {
    return normalized;
  }
  return `${normalized.slice(0, 45).trimEnd()}...`;
};

const readStoredThinkingEffort = (userScope: string): ThinkingEffort => {
  if (typeof window === "undefined") {
    return DEFAULT_THINKING_EFFORT;
  }

  const stored = window.localStorage.getItem(buildThinkingEffortStorageKey(userScope));
  return stored === "low" || stored === "medium" || stored === "high" ? stored : DEFAULT_THINKING_EFFORT;
};

const writeStoredThinkingEffort = (userScope: string, value: ThinkingEffort) => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(buildThinkingEffortStorageKey(userScope), value);
};

const formatChatSessionLabel = (session: ChatSessionRecord) => {
  const timestamp = new Date(session.updatedAt).toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `${session.title} · ${timestamp}`;
};

const NUCLEUS_SCHEMA_FIELDS = [
  "createchatthread",
  "getchatthread",
  "getchateventsbythread",
  "appendchatevent",
  "onchatevent",
];

const NUCLEUS_SCHEMA_TYPES = ["chatthreadstatus", "chatthread", "chatevent", "chateventconnection"];
const NUCLEUS_BACKEND_FIELDS = ["startnucleusturn"];

const getNucleusSchemaMessage = (error: unknown): string | null => {
  const message = getNormalizedErrorMessage(error).toLowerCase();
  if (!message) {
    return null;
  }

  const mentionsNucleusField = NUCLEUS_SCHEMA_FIELDS.some((field) => message.includes(field));
  const mentionsNucleusType = NUCLEUS_SCHEMA_TYPES.some((typeName) => message.includes(typeName));
  const looksLikeSchemaError =
    message.includes("unknowntype") ||
    message.includes("unknown type") ||
    message.includes("fieldundefined") ||
    message.includes("field undefined") ||
    message.includes("unknown field") ||
    message.includes("cannot query field") ||
    message.includes("schema");

  if ((!mentionsNucleusField && !mentionsNucleusType) || !looksLikeSchemaError) {
    return null;
  }

  return "Nucleus is not fully available in this environment yet. Some live features may be limited for now.";
};

const getNucleusBackendMessage = (error: unknown): string | null => {
  const message = getNormalizedErrorMessage(error).toLowerCase();
  if (!message) {
    return null;
  }

  const mentionsBackendField = NUCLEUS_BACKEND_FIELDS.some((field) => message.includes(field));
  const looksLikeSchemaError =
    message.includes("unknowntype") ||
    message.includes("unknown type") ||
    message.includes("fieldundefined") ||
    message.includes("field undefined") ||
    message.includes("unknown field") ||
    message.includes("cannot query field") ||
    message.includes("schema");

  if (!mentionsBackendField || !looksLikeSchemaError) {
    return null;
  }

  return "Nucleus could not reach its live backend path in this environment. Some responses may use a limited fallback path.";
};

const isTurnIdArgumentUnsupported = (error: unknown) => {
  const message = getNormalizedErrorMessage(error).toLowerCase();
  return (
    message.includes("turnid") &&
    (message.includes("unknown argument") ||
      message.includes("fieldundefined") ||
      message.includes("field undefined") ||
      message.includes("cannot query field") ||
      message.includes("schema"))
  );
};

const parsePayload = (payload: string): Record<string, unknown> => {
  try {
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const isSyncWarningMessage = (message: string | null) =>
  message === SYNC_REFRESH_FAILURE_MESSAGE || message === SYNC_TIMEOUT_MESSAGE;

const clearSyncWarning = (message: string | null) => (isSyncWarningMessage(message) ? null : message);

const shouldClearPendingTurn = (eventType: ChatEvent["eventType"]) =>
  [
    "USER_MESSAGE",
    "TEXT_MESSAGE_START",
    "TEXT_MESSAGE_CONTENT",
    "TEXT_MESSAGE_DELTA",
    "RUN_ERROR",
    "RUN_FINISHED",
  ].includes(eventType);

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const PENDING_MATCH_WINDOW_MS = 10_000;

const describeActivity = (event: ChatEvent) => {
  const payload = parsePayload(event.payload);

  switch (event.eventType) {
    case "RUN_STARTED":
      return "Turn started";
    case "RUN_FINISHED":
      return "Turn finished";
    case "ACTIVITY_SNAPSHOT":
      return `${String(payload.label ?? "Activity")}: ${String(payload.detail ?? "")}`;
    case "REASONING_MESSAGE_CONTENT":
      return String(payload.delta ?? "");
    case "STEP_STARTED":
      return `Step started: ${String(payload.label ?? payload.stepId ?? "")}`;
    case "STEP_FINISHED":
      return `Step finished: ${String(payload.summary ?? payload.label ?? payload.stepId ?? "")}`;
    case "TOOL_CALL_START":
      return `Tool started: ${String(payload.displayLabel ?? payload.toolName ?? "")}`;
    case "TOOL_CALL_ARGS":
      return `Tool input: ${String(payload.displayLabel ?? payload.toolName ?? "")}`;
    case "TOOL_CALL_END":
      return `Tool finished: ${String(payload.displayLabel ?? payload.toolName ?? "")}`;
    case "TOOL_CALL_RESULT":
      return `Tool result: ${String(payload.displayLabel ?? payload.toolName ?? "")}`;
    case "RUN_ERROR":
      return `Runtime error: ${String(payload.message ?? "")}`;
    default:
      return `${event.eventType}: ${event.payload}`;
  }
};

const formatActivityDetails = (event: ChatEvent) => {
  const payload = parsePayload(event.payload);

  switch (event.eventType) {
    case "RUN_STARTED":
      return payload.input ? JSON.stringify(payload.input, null, 2) : null;
    case "TOOL_CALL_ARGS":
      return payload.argsSummary ? JSON.stringify(payload.argsSummary, null, 2) : null;
    case "TOOL_CALL_END":
    case "TOOL_CALL_RESULT":
      return payload.resultSummary ? JSON.stringify(payload.resultSummary, null, 2) : null;
    default:
      return null;
  }
};

const formatTurnTimestamp = (createdAt: string) =>
  new Date(createdAt).toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const getComposerStatusText = (
  isBootstrapping: boolean,
  isSending: boolean,
  hasActiveTurn: boolean,
  latestTurnView: TurnViewModel | null,
  queuedTurnsCount: number
) => {
  if (isBootstrapping) {
    return "Opening the conversation for this run.";
  }

  if (queuedTurnsCount > 0 && latestTurnView?.isRunning) {
    return `${queuedTurnsCount} queued next while Nucleus is working.`;
  }

  if (queuedTurnsCount > 0 && !hasActiveTurn && !isSending) {
    return `${queuedTurnsCount} queued question${queuedTurnsCount === 1 ? "" : "s"} ready to send.`;
  }

  if (latestTurnView?.isRunning) {
    return "Nucleus is working. You can queue another question while this runs.";
  }

  if (hasActiveTurn || isSending) {
    return "Nucleus is working on your question.";
  }

  return "";
};

const buildThreadSequence = (threadId: string, sequence: number) =>
  `${threadId}#${String(sequence).padStart(12, "0")}`;

const getWorkSummaryLabel = (viewModel: TurnViewModel) => {
  const parts: string[] = [];
  if (viewModel.toolCount > 0) {
    parts.push(`${viewModel.toolCount} tool${viewModel.toolCount === 1 ? "" : "s"}`);
  }
  if (viewModel.reasoningCount > 0) {
    parts.push(`${viewModel.reasoningCount} reasoning note${viewModel.reasoningCount === 1 ? "" : "s"}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "Turn details";
};

const WorkIcon = ({
  state,
}: {
  state: "working" | "complete" | "error";
}) => {
  if (state === "error") {
    return <AlertCircle size={14} />;
  }
  if (state === "complete") {
    return <CheckCircle2 size={14} />;
  }
  return <Wrench size={14} />;
};

const PendingTurnCard: FC<{ pendingTurn: PendingTurn }> = ({ pendingTurn }) => (
  <div css={styles.turnGroup}>
    <div css={styles.userRow}>
      <div css={styles.userBubble}>
        <p css={styles.userMeta}>You</p>
        <p css={styles.userText}>{pendingTurn.text}</p>
      </div>
    </div>

    <div css={styles.assistantCard("working")}>
      <div css={styles.assistantHeader}>
        <p css={styles.assistantMeta}>Nucleus</p>
        <span css={styles.statusChip("working")}>
          <Loader2 size={12} css={styles.spinningIcon} />
          Thinking
        </span>
      </div>
      <div css={styles.placeholderAnswer}>
        <Loader2 size={14} />
        Thinking...
      </div>
    </div>
  </div>
);

const StreamingAnswerText: FC<{ text: string }> = ({ text }) => {
  const [visibleText, setVisibleText] = useState(text);

  useEffect(() => {
    setVisibleText((current) => {
      if (!text.startsWith(current)) {
        return text;
      }
      return current;
    });
  }, [text]);

  useEffect(() => {
    if (visibleText === text) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setVisibleText((current) => {
        if (current === text) {
          return current;
        }
        const remaining = text.length - current.length;
        const step = Math.max(1, Math.min(12, Math.ceil(remaining / 10)));
        return text.slice(0, current.length + step);
      });
    }, STREAM_REVEAL_TICK_MS);

    return () => window.clearTimeout(timeoutId);
  }, [text, visibleText]);

  return <p css={styles.streamingAnswer}>{visibleText}</p>;
};

const isDebugUiEnabled = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return new URLSearchParams(window.location.search).get("nucleusDebug") === "1";
};

const isFormatTraceEnabled = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return new URLSearchParams(window.location.search).get("nucleusFormatTrace") === "1";
};

const summarizeFormattingSignals = (text: string) => ({
  chars: text.length,
  doubleAsterisks: (text.match(/\*\*/g) ?? []).length,
  doubleUnderscores: (text.match(/__/g) ?? []).length,
  tablePipes: (text.match(/\|/g) ?? []).length,
  headingMarkers: (text.match(/\n#/g) ?? []).length,
  bulletMarkers: (text.match(/\n- /g) ?? []).length,
  preview: text.replace(/\s+/g, " ").slice(0, 220),
});

const CHART_COLORS = [
  theme.colors.nucleus,
  theme.colors.blue500,
  theme.colors.green500,
  theme.colors.orange500,
  theme.colors.red600,
  "#7c3aed",
];

type DownloadableArtifact = Extract<TurnArtifact, { type: "table" | "chart" }>;
type ArtifactWithDownloads = DownloadableArtifact & {
  artifact_id: string;
  downloads: ArtifactDownload[];
};

type GeneratedArtifactItem = {
  key: string;
  turnId: string;
  artifact: ArtifactWithDownloads;
};

const getArtifactKey = (turnId: string, artifact: TurnArtifact, index: number) => {
  if ((artifact.type === "table" || artifact.type === "chart") && artifact.artifact_id) {
    return artifact.artifact_id;
  }
  if (artifact.type === "csv") {
    return `${turnId}:csv:${artifact.file_name}:${index}`;
  }
  return `${turnId}:${artifact.type}:${artifact.title}:${index}`;
};

const hasDownloadButtons = (artifact: TurnArtifact): artifact is ArtifactWithDownloads =>
  (artifact.type === "table" || artifact.type === "chart") &&
  Array.isArray(artifact.downloads) &&
  artifact.downloads.length > 0;

const normalizeChartValue = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : value;
  }
  return value ?? "";
};

const buildChartSeries = (artifact: ChartArtifact) => {
  if (!artifact.series_key) {
    return {
      data: artifact.data.map((row) => ({
        ...row,
        [artifact.y_key]: normalizeChartValue(row[artifact.y_key]),
      })),
      seriesKeys: [artifact.y_key],
    };
  }

  const grouped = new Map<string, Record<string, unknown>>();
  const seriesOrder: string[] = [];
  for (const row of artifact.data) {
    const xValue = String(row[artifact.x_key] ?? "");
    const seriesValue = String(row[artifact.series_key] ?? "Series");
    const entry = grouped.get(xValue) ?? { [artifact.x_key]: row[artifact.x_key] ?? "" };
    entry[seriesValue] = normalizeChartValue(row[artifact.y_key]);
    grouped.set(xValue, entry);
    if (!seriesOrder.includes(seriesValue)) {
      seriesOrder.push(seriesValue);
    }
  }

  return {
    data: [...grouped.values()],
    seriesKeys: seriesOrder,
  };
};

const downloadCsvArtifact = (artifact: CsvArtifact) => {
  const rows = [
    artifact.columns.join(","),
    ...artifact.rows.map((row) =>
      artifact.columns
        .map((column) => {
          const value = row[column];
          const serialized = value === null || value === undefined ? "" : String(value);
          return /[",\n]/.test(serialized) ? `"${serialized.replace(/"/g, '""')}"` : serialized;
        })
        .join(",")
    ),
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = artifact.file_name;
  link.click();
  URL.revokeObjectURL(url);
};

const formatArtifactCellValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toLocaleString() : String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value);
};

const isUserEditedArtifactRow = (row: Record<string, unknown>) =>
  String(row.source ?? "").trim().toLowerCase() === "user";

const ArtifactTablePreview: FC<{ artifact: CsvArtifact | TableArtifact }> = ({ artifact }) => {
  const previewRows = artifact.rows.slice(0, CSV_PREVIEW_ROW_LIMIT);
  if (previewRows.length === 0) {
    return null;
  }
  return (
    <div css={styles.artifactTableWrap}>
      <table css={styles.artifactTable}>
        <thead>
          <tr>
            {artifact.columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {previewRows.map((row, rowIndex) => (
            <tr key={rowIndex} css={styles.artifactTableRow(isUserEditedArtifactRow(row))}>
              {artifact.columns.map((column) => (
                <td key={column}>{formatArtifactCellValue(row[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ArtifactCard: FC<{
  artifact: TurnArtifact;
  onDownload?: (artifact: DownloadableArtifact, download: ArtifactDownload) => void | Promise<void>;
}> = ({ artifact, onDownload }) => {
  if (artifact.type === "csv" || artifact.type === "table") {
    const totalRows = artifact.rows_total_count ?? artifact.rows.length;
    const isPartialPayload = totalRows > artifact.rows.length;
    const subtitle =
      artifact.description ??
      (isPartialPayload
        ? `${artifact.rows.length} preview rows available from ${totalRows.toLocaleString()} total rows.`
        : `${artifact.rows.length.toLocaleString()} rows ready to download as CSV.`);

    return (
      <div css={styles.artifactCard}>
        <div css={styles.artifactHeader}>
          <div css={styles.artifactTitleWrap}>
            <p css={styles.artifactTitle}>{artifact.title}</p>
            <p css={styles.artifactSubtitle}>{subtitle}</p>
          </div>
          {hasDownloadButtons(artifact) ? (
            <div css={styles.artifactMetaRow}>
              {artifact.downloads.map((download) => (
                <button
                  key={`${artifact.artifact_id}-${download.format}`}
                  type="button"
                  css={styles.artifactAction}
                  onClick={() => {
                    void onDownload?.(artifact, download);
                  }}
                >
                  <Download size={14} />
                  Download {download.format.toUpperCase()}
                </button>
              ))}
            </div>
          ) : artifact.type === "csv" ? (
            <button type="button" css={styles.artifactAction} onClick={() => downloadCsvArtifact(artifact)}>
              <Download size={14} />
              {isPartialPayload ? "Download Preview CSV" : "Download CSV"}
            </button>
          ) : null}
        </div>
        <div css={styles.artifactMetaRow}>
          <span css={styles.artifactMetaChip}>
            <FileSpreadsheet size={12} />
            {artifact.type === "csv" ? artifact.file_name : artifact.dataset_name}
          </span>
          <span css={styles.artifactMetaChip}>{artifact.columns.length} columns</span>
          <span css={styles.artifactMetaChip}>
            {isPartialPayload
              ? `${artifact.rows.length.toLocaleString()} of ${totalRows.toLocaleString()} rows`
              : `${artifact.rows.length.toLocaleString()} rows`}
          </span>
        </div>
        <ArtifactTablePreview artifact={artifact} />
      </div>
    );
  }

  const { data, seriesKeys } = buildChartSeries(artifact);
  return (
    <div css={styles.artifactCard}>
      <div css={styles.artifactHeader}>
        <div css={styles.artifactTitleWrap}>
          <p css={styles.artifactTitle}>{artifact.title}</p>
          <p css={styles.artifactSubtitle}>
            {artifact.description ?? `${artifact.chart_type} chart from ${artifact.dataset_name}.`}
          </p>
        </div>
      </div>
      <div css={styles.artifactMetaRow}>
        <span css={styles.artifactMetaChip}>
          <BarChart3 size={12} />
          {artifact.chart_type}
        </span>
        <span css={styles.artifactMetaChip}>{data.length} points</span>
        <span css={styles.artifactMetaChip}>{artifact.x_key}</span>
        <span css={styles.artifactMetaChip}>{artifact.y_key}</span>
        {hasDownloadButtons(artifact) ? (
          <>
            {artifact.downloads.map((download) => (
              <button
                key={`${artifact.artifact_id}-${download.format}`}
                type="button"
                css={styles.artifactAction}
                onClick={() => {
                  void onDownload?.(artifact, download);
                }}
              >
                <Download size={14} />
                Download data {download.format.toUpperCase()}
              </button>
            ))}
          </>
        ) : null}
      </div>
      <ChartContainer config={{}} css={styles.chartFrame}>
        {artifact.chart_type === "line" ? (
          <LineChart data={data}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey={artifact.x_key} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} />
            <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
            {seriesKeys.length > 1 ? <Legend /> : null}
            {seriesKeys.map((seriesKey, index) => (
              <Line
                key={seriesKey}
                type="monotone"
                dataKey={seriesKey}
                stroke={CHART_COLORS[index % CHART_COLORS.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        ) : artifact.chart_type === "area" ? (
          <AreaChart data={data}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey={artifact.x_key} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} />
            <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
            {seriesKeys.length > 1 ? <Legend /> : null}
            {seriesKeys.map((seriesKey, index) => (
              <Area
                key={seriesKey}
                type="monotone"
                dataKey={seriesKey}
                stroke={CHART_COLORS[index % CHART_COLORS.length]}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                fillOpacity={0.18}
              />
            ))}
          </AreaChart>
        ) : artifact.chart_type === "scatter" ? (
          <ScatterChart>
            <CartesianGrid />
            <XAxis type="category" dataKey={artifact.x_key} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis type="number" dataKey={artifact.y_key} tickLine={false} axisLine={false} />
            <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
            <Scatter data={data} fill={CHART_COLORS[0]} />
          </ScatterChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey={artifact.x_key} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} />
            <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
            {seriesKeys.length > 1 ? <Legend /> : null}
            {seriesKeys.map((seriesKey, index) => (
              <Bar
                key={seriesKey}
                dataKey={seriesKey}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        )}
      </ChartContainer>
    </div>
  );
};

const getArtifactTotalRows = (artifact: DownloadableArtifact) => {
  if (artifact.type === "chart") {
    return artifact.rows_total_count ?? artifact.data.length;
  }
  return artifact.rows_total_count ?? artifact.rows.length;
};

const GeneratedFileCard: FC<{
  artifact: ArtifactWithDownloads;
  onDownload: (artifact: DownloadableArtifact, download: ArtifactDownload) => void | Promise<void>;
}> = ({ artifact, onDownload }) => {
  const rowCount = getArtifactTotalRows(artifact);
  const formatLabels = artifact.downloads.map((download) => download.format.toUpperCase()).join(", ");

  return (
    <div css={styles.generatedFileCard}>
      <div css={styles.generatedFileText}>
        <p css={styles.generatedFileTitle}>{artifact.title}</p>
        <p css={styles.generatedFileMeta}>
          {artifact.folder}/{artifact.dataset_name} · {rowCount.toLocaleString()} rows · {formatLabels}
        </p>
      </div>
      <div css={styles.generatedFileActions}>
        {artifact.downloads.map((download) => (
          <button
            key={`${artifact.artifact_id}-${download.format}`}
            type="button"
            css={styles.artifactAction}
            onClick={() => {
              void onDownload(artifact, download);
            }}
          >
            <Download size={14} />
            {download.format.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
};

const NucleusBrand: FC<{ size?: "hero" | "compact"; showIcon?: boolean }> = ({ size = "compact", showIcon = true }) => (
  <div css={styles.brandLockup}>
    {showIcon ? <Atom size={size === "hero" ? 34 : 27} css={styles.heroIcon} /> : null}
    <span css={size === "hero" ? styles.heroTitle : styles.brandTitle}>Nucleus</span>
    <span css={styles.betaBadge}>Beta</span>
  </div>
);

const buildGreetingName = (emailOrId?: string | null) => {
  const rawName = String(emailOrId ?? "").split("@")[0]?.trim();
  const firstName = rawName?.split(/[._+\-\s]/).filter(Boolean)[0] ?? "";
  if (!firstName) {
    return "there";
  }
  return firstName.toLowerCase();
};

const NucleusEmptyHero: FC<{
  mode: "select-run" | "ready";
  isBootstrapping: boolean;
  shellMode: NucleusChatShellMode;
  greetingName: string;
}> = ({ mode, isBootstrapping, shellMode, greetingName }) => (
  <div css={styles.heroPanel(shellMode)}>
    <div css={styles.heroSignal}>
      <div css={styles.heroSignalCore}>
        {isBootstrapping ? <Loader2 size={28} css={styles.spinningIcon} /> : <Atom size={34} css={styles.heroIcon} />}
      </div>
    </div>
    <p css={styles.heroGreeting}>
      hi {greetingName}
      <span css={styles.typingIndicator} aria-hidden="true">
        <span css={styles.typingCursor} />
      </span>
    </p>
    <p css={styles.heroSubtitle}>
      {mode === "ready"
        ? "Ask what changed, what is at risk, why something is short, how settings shaped the run, or request a table or export."
        : "Select a completed run so Nucleus can inspect its inputs, outputs, settings, planner edits, and documentation."}
    </p>
  </div>
);

export const NucleusChatView: FC<NucleusChatViewProps> = ({
  shellMode = "page",
  currentViewLabel = null,
}) => {
  const { user } = useAuth();
  const { selectedCustomerId } = useCustomer();
  const { loadingRuns, runSettingsJson, selectedRun, selectedRunId, setSelectedRunId, sortedRuns } = useRunSelector();
  const [events, setEvents] = useState<ChatEvent[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [modeNotice, setModeNotice] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [transportMode, setTransportMode] = useState<"appsync" | "local">("appsync");
  const [pendingTurn, setPendingTurn] = useState<PendingTurn | null>(null);
  const [queuedTurns, setQueuedTurns] = useState<QueuedTurn[]>([]);
  const [activeTurnBaselineSequence, setActiveTurnBaselineSequence] = useState<number | null>(null);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSessionRecord[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [thinkingEffort, setThinkingEffort] = useState<ThinkingEffort>(DEFAULT_THINKING_EFFORT);
  const sequenceRef = useRef(1);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const activeChatSessionTitleRef = useRef(DEFAULT_SESSION_TITLE);
  const submitLockRef = useRef(false);
  const queueDispatchRef = useRef<string | null>(null);
  const lastSubmittedRef = useRef<{ signature: string; submittedAt: number } | null>(null);
  const lastFormatTraceSignatureRef = useRef<string | null>(null);
  const debugUiEnabled = useMemo(() => isDebugUiEnabled(), []);
  const formatTraceEnabled = useMemo(() => isFormatTraceEnabled(), []);
  const userThreadScope = useMemo(
    () => sanitizeThreadScopePart(user?.id ?? user?.email ?? "anonymous"),
    [user?.email, user?.id]
  );
  const createdByLabel = user?.email ?? user?.id ?? "nucleus-webapp";
  const greetingName = useMemo(() => buildGreetingName(user?.email ?? user?.id), [user?.email, user?.id]);

  useEffect(() => {
    setThinkingEffort(readStoredThinkingEffort(userThreadScope));
  }, [userThreadScope]);

  useEffect(() => {
    if (!selectedRun?.id) {
      setThreadId(null);
      setChatSessions([]);
      setEvents([]);
      setPendingTurn(null);
      setActiveTurnBaselineSequence(null);
      setActiveTurnId(null);
      setQueuedTurns([]);
      sequenceRef.current = 1;
      return;
    }

    const { sessions, activeThreadId } = ensureChatSessions(selectedRun.id, userThreadScope);
    setChatSessions(sessions);
    setThreadId(activeThreadId);
    setEvents([]);
    setPendingTurn(null);
    setActiveTurnBaselineSequence(null);
    setActiveTurnId(null);
    setQueuedTurns([]);
    sequenceRef.current = 1;
  }, [selectedRun?.id, userThreadScope]);

  const contextId = selectedRun?.id ? `nucleus:${selectedRun.id}` : "nucleus:unscoped";
  const runLabel = selectedRun ? formatRunOptionLabel(selectedRun) : "selected run";
  const activeChatSession = useMemo(
    () => chatSessions.find((session) => session.threadId === threadId) ?? null,
    [chatSessions, threadId]
  );
  useEffect(() => {
    activeChatSessionTitleRef.current = activeChatSession?.title ?? DEFAULT_SESSION_TITLE;
  }, [activeChatSession?.title]);
  const updateActiveChatSession = useCallback(
    (updater: (session: ChatSessionRecord) => ChatSessionRecord) => {
      if (!selectedRun?.id || !threadId) {
        return;
      }
      setChatSessions(updateStoredChatSession(selectedRun.id, userThreadScope, threadId, updater));
    },
    [selectedRun?.id, threadId, userThreadScope]
  );
  const handleChatSessionChange = useCallback(
    (nextThreadId: string) => {
      if (!selectedRun?.id) {
        return;
      }
      writeActiveChatSessionThreadId(selectedRun.id, userThreadScope, nextThreadId);
      setThreadId(nextThreadId);
      setEvents([]);
      setPendingTurn(null);
      setQueuedTurns([]);
      setActiveTurnBaselineSequence(null);
      setActiveTurnId(null);
      setError(null);
      sequenceRef.current = 1;
    },
    [selectedRun?.id, userThreadScope]
  );
  const handleNewChat = useCallback(() => {
    if (!selectedRun?.id) {
      return;
    }
    const next = createStoredChatSession(selectedRun.id, userThreadScope);
    setChatSessions(next.sessions);
    setThreadId(next.session.threadId);
    setEvents([]);
    setPendingTurn(null);
    setQueuedTurns([]);
    setActiveTurnBaselineSequence(null);
    setActiveTurnId(null);
    setError(null);
    sequenceRef.current = 1;
  }, [selectedRun?.id, userThreadScope]);
  const handleThinkingEffortChange = useCallback(
    (value: string) => {
      const nextValue: ThinkingEffort = value === "low" || value === "high" ? value : "medium";
      setThinkingEffort(nextValue);
      writeStoredThinkingEffort(userThreadScope, nextValue);
    },
    [userThreadScope]
  );

  const turns = useMemo(() => buildTurnSummaries(events), [events]);
  const nonLegacyTurns = useMemo(() => turns.filter((turn) => !turn.isLegacyFake), [turns]);
  const { hiddenTurns, visibleTurns } = useMemo(
    () =>
      limitVisibleTurns(
        nonLegacyTurns,
        shellMode === "dock" ? MAX_VISIBLE_DOCK_TURNS : MAX_VISIBLE_PAGE_TURNS
      ),
    [nonLegacyTurns, shellMode]
  );
  const hiddenTurnPreviews = useMemo(() => hiddenTurns.map((turn) => buildHiddenTurnPreview(turn)), [hiddenTurns]);
  const turnViewModels = useMemo(() => visibleTurns.map((turn) => buildTurnViewModel(turn)), [visibleTurns]);
  const allTurnViewModels = useMemo(() => nonLegacyTurns.map((turn) => buildTurnViewModel(turn)), [nonLegacyTurns]);
  const visibleArtifactKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const viewModel of turnViewModels) {
      viewModel.artifacts.forEach((artifact, index) => {
        keys.add(getArtifactKey(viewModel.turn.turnId, artifact, index));
      });
    }
    return keys;
  }, [turnViewModels]);
  const generatedArtifactItems = useMemo<GeneratedArtifactItem[]>(() => {
    const items: GeneratedArtifactItem[] = [];
    const seenKeys = new Set<string>();

    for (const viewModel of [...allTurnViewModels].reverse()) {
      viewModel.artifacts.forEach((artifact, index) => {
        if (!hasDownloadButtons(artifact)) {
          return;
        }
        const key = getArtifactKey(viewModel.turn.turnId, artifact, index);
        if (seenKeys.has(key)) {
          return;
        }
        seenKeys.add(key);
        items.push({ key, turnId: viewModel.turn.turnId, artifact });
      });
    }

    return items;
  }, [allTurnViewModels]);
  const shelfArtifactItems = useMemo(
    () => generatedArtifactItems.filter((item) => !visibleArtifactKeys.has(item.key)).slice(0, 3),
    [generatedArtifactItems, visibleArtifactKeys]
  );
  const latestTurnView = turnViewModels.at(-1) ?? null;
  const latestTurnEvents = latestTurnView?.turn.activityEvents ?? [];
  const hasActiveTurn = Boolean(pendingTurn) || Boolean(activeTurnBaselineSequence) || Boolean(latestTurnView?.isRunning);
  const showHeroState = Boolean(selectedRun && runSettingsJson && turnViewModels.length === 0 && !pendingTurn);
  const showLandingState = !selectedRun || !runSettingsJson || showHeroState;
  const showFooterStatus =
    queuedTurns.length > 0 || hasActiveTurn || isSending || isBootstrapping;
  const footerStatusText = getComposerStatusText(isBootstrapping, isSending, hasActiveTurn, latestTurnView, queuedTurns.length);

  useEffect(() => {
    if (!formatTraceEnabled || !latestTurnView) {
      return;
    }

    const rawAssistantText = latestTurnView.turn.assistantMessage?.text ?? "";
    const displayedAssistantText = latestTurnView.assistantText;
    const signature = JSON.stringify({
      turnId: latestTurnView.turn.turnId,
      rawLength: rawAssistantText.length,
      displayedLength: displayedAssistantText.length,
      renderMode: latestTurnView.assistantRenderMode,
      isRunning: latestTurnView.isRunning,
    });

    if (lastFormatTraceSignatureRef.current === signature) {
      return;
    }

    lastFormatTraceSignatureRef.current = signature;
    console.debug("[NucleusFormatTrace] appsync_raw_text", {
      turnId: latestTurnView.turn.turnId,
      ...summarizeFormattingSignals(rawAssistantText),
    });
    console.debug("[NucleusFormatTrace] frontend_display_text", {
      turnId: latestTurnView.turn.turnId,
      renderMode: latestTurnView.assistantRenderMode,
      isRunning: latestTurnView.isRunning,
      ...summarizeFormattingSignals(displayedAssistantText),
    });
  }, [formatTraceEnabled, latestTurnView]);

  useEffect(() => {
    if (!pendingTurn) {
      return;
    }

    const pendingCreatedAtMs = Date.parse(pendingTurn.createdAt);
    const matchingTurn = turns.find((turn) => {
      const userText = turn.userMessage?.text?.trim();
      if (!userText || userText !== pendingTurn.text.trim()) {
        return false;
      }

      const turnCreatedAtMs = Date.parse(turn.createdAt);
      if (Number.isNaN(pendingCreatedAtMs) || Number.isNaN(turnCreatedAtMs)) {
        return true;
      }

      return Math.abs(turnCreatedAtMs - pendingCreatedAtMs) <= PENDING_MATCH_WINDOW_MS || turnCreatedAtMs >= pendingCreatedAtMs;
    });

    if (matchingTurn) {
      setPendingTurn(null);
    }
  }, [pendingTurn, turns]);

  useEffect(() => {
    const transcriptNode = transcriptRef.current;
    if (!transcriptNode) {
      return;
    }

    const distanceFromBottom =
      transcriptNode.scrollHeight - transcriptNode.scrollTop - transcriptNode.clientHeight;
    const shouldStickToBottom = hasActiveTurn || distanceFromBottom < 96;
    if (!shouldStickToBottom) {
      return;
    }

    transcriptNode.scrollTo({
      top: transcriptNode.scrollHeight,
      behavior: hasActiveTurn ? "auto" : "smooth",
    });
  }, [events.length, latestTurnView?.assistantText, latestTurnView?.statusLabel, hasActiveTurn]);

  useEffect(() => {
    if (!threadId || !runSettingsJson) {
      setEvents([]);
      setPendingTurn(null);
      setActiveTurnBaselineSequence(null);
      setActiveTurnId(null);
      sequenceRef.current = 1;
      return;
    }

    let isDisposed = false;
    let subscription: { unsubscribe: () => void } | null = null;

    const bootstrap = async () => {
      setIsBootstrapping(true);
      setError(null);
      setModeNotice(null);
      setTransportMode("appsync");

      try {
        const now = new Date().toISOString();

        await appSyncService.createChatThread({
          organizationId: selectedCustomerId,
          threadId,
          contextId,
          runSettings: runSettingsJson,
          status: ChatThreadStatus.ACTIVE,
          title: activeChatSessionTitleRef.current,
          createdBy: createdByLabel,
          createdAt: now,
          updatedAt: now,
        });

        if (isDisposed) {
          return;
        }

        const sortedEvents = await fetchThreadEvents(threadId, selectedCustomerId);
        setEvents(sortedEvents);
        if (sortedEvents.length > 0 && selectedRun?.id) {
          const firstUserEvent = sortedEvents.find((event) => event.eventType === "USER_MESSAGE");
          const firstUserText = firstUserEvent
            ? String(parsePayload(firstUserEvent.payload).text ?? "").trim()
            : "";
          const nextTitle =
            firstUserText && activeChatSessionTitleRef.current === DEFAULT_SESSION_TITLE
              ? buildChatSessionTitle(firstUserText)
              : activeChatSessionTitleRef.current;
          setChatSessions(
            updateStoredChatSession(selectedRun.id, userThreadScope, threadId, (session) => ({
              ...session,
              title: nextTitle,
              updatedAt: sortedEvents.at(-1)?.createdAt ?? session.updatedAt,
            }))
          );
        }
        if (sortedEvents.some((event) => event.eventType === "USER_MESSAGE")) {
          setPendingTurn(null);
        }
        setError((current) => clearSyncWarning(current));
        sequenceRef.current =
          sortedEvents.reduce((max, event) => Math.max(max, event.sequence), 0) + 1;

        subscription = await appSyncService.subscribeToChatEvents(
          {
            organizationId: selectedCustomerId,
            threadId,
          },
          (incomingEvent) => {
            if (isDisposed) {
              return;
            }
            setEvents((current) => mergeEvent(current, incomingEvent));
            if (
              selectedRun?.id &&
              ["USER_MESSAGE", "RUN_FINISHED", "RUN_ERROR"].includes(incomingEvent.eventType)
            ) {
              setChatSessions(
                updateStoredChatSession(selectedRun.id, userThreadScope, threadId, (session) => ({
                  ...session,
                  updatedAt: incomingEvent.createdAt,
                }))
              );
            }
            if (shouldClearPendingTurn(incomingEvent.eventType)) {
              setPendingTurn(null);
            }
            if (
              [
                "RUN_STARTED",
                "TEXT_MESSAGE_START",
                "TEXT_MESSAGE_CONTENT",
                "TEXT_MESSAGE_DELTA",
                "RUN_FINISHED",
                "RUN_ERROR",
              ].includes(incomingEvent.eventType)
            ) {
              setError((current) => clearSyncWarning(current));
            }
            sequenceRef.current = Math.max(sequenceRef.current, incomingEvent.sequence + 1);
          },
          (subscriptionError) => {
            if (!isDisposed) {
              const schemaMessage = getNucleusSchemaMessage(subscriptionError);
              if (schemaMessage) {
                setTransportMode("local");
                setModeNotice(schemaMessage);
                setError(null);
                return;
              }

              setError(getNormalizedErrorMessage(subscriptionError) || "Nucleus subscription failed");
            }
          }
        );
      } catch (bootstrapError) {
        if (!isDisposed) {
          const schemaMessage = getNucleusSchemaMessage(bootstrapError);
          if (schemaMessage) {
            setTransportMode("local");
            setModeNotice(schemaMessage);
            setError(null);
            setEvents([]);
            setPendingTurn(null);
            setActiveTurnBaselineSequence(null);
            setActiveTurnId(null);
            sequenceRef.current = 1;
          } else {
            setError(getNormalizedErrorMessage(bootstrapError) || "Failed to initialize Nucleus thread");
          }
        }
      } finally {
        if (!isDisposed) {
          setIsBootstrapping(false);
        }
      }
    };

    void bootstrap();

    return () => {
      isDisposed = true;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [contextId, createdByLabel, runSettingsJson, selectedCustomerId, selectedRun?.id, threadId, userThreadScope]);

  const getNextSequence = useCallback(() => {
    const sequence = sequenceRef.current;
    sequenceRef.current += 1;
    return sequence;
  }, []);

  const handleArtifactDownload = useCallback(
    async (turnId: string, artifact: DownloadableArtifact, download: ArtifactDownload) => {
      try {
        const { data } = await apolloClient.query<
          { getSignedS3Url?: string | null },
          { s3Url: string; expiresInSeconds: number }
        >({
          query: GET_SIGNED_S3_URL,
          variables: { s3Url: download.s3_uri, expiresInSeconds: 600 },
          fetchPolicy: "network-only",
        });

        const signedUrl = data?.getSignedS3Url;
        if (!signedUrl) {
          throw new Error("No signed download URL was returned.");
        }

        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = download.file_name;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (downloadError) {
        setError(getNormalizedErrorMessage(downloadError) || "Failed to prepare the artifact download.");
        return;
      }

      if (!threadId) {
        return;
      }

      try {
        const sequence = getNextSequence();
        const createdAt = new Date().toISOString();
        const appendedEvent = await appSyncService.appendChatEvent({
          organizationId: selectedCustomerId,
          threadId,
          turnId,
          eventId: crypto.randomUUID(),
          threadSequence: buildThreadSequence(threadId, sequence),
          sequence,
          eventType: "ARTIFACT_DOWNLOAD",
          payload: JSON.stringify({
            artifactId: artifact.artifact_id ?? `${turnId}-${artifact.type}`,
            artifactTitle: artifact.title,
            datasetName: artifact.dataset_name,
            folder: artifact.folder,
            format: download.format,
            fileName: download.file_name,
            rowCount:
              artifact.type === "chart"
                ? artifact.rows_total_count ?? artifact.data.length
                : artifact.rows_total_count ?? artifact.rows.length,
            columnCount:
              artifact.type === "chart"
                ? artifact.columns?.length ?? Object.keys(artifact.data[0] ?? {}).length
                : artifact.columns.length,
            actorId: user?.id ?? createdByLabel,
            actorEmail: createdByLabel,
            userId: user?.id ?? createdByLabel,
            source: "web",
            contextId,
            runSettings: runSettingsJson,
          }),
          createdAt,
        });
        setEvents((current) => mergeEvent(current, appendedEvent));
      } catch (notificationError) {
        console.warn("Failed to record Nucleus artifact download:", notificationError);
      }
    },
    [contextId, createdByLabel, getNextSequence, runSettingsJson, selectedCustomerId, threadId, user?.id]
  );

  const refreshThreadEvents = useCallback(async (minimumSequence?: number, expectedTurnId?: string | null) => {
    if (!threadId) {
      return { hasNewEvents: false, isTerminal: false, activeTurnId: expectedTurnId ?? null, hasVisibleProgress: false };
    }

    const sortedEvents = await fetchThreadEvents(threadId, selectedCustomerId);
    const newEvents =
      minimumSequence === undefined
        ? sortedEvents
        : sortedEvents.filter((chatEvent) => chatEvent.sequence > minimumSequence);
    const hasNewEvents = newEvents.length > 0;
    const latestStartedTurnId =
      [...newEvents].reverse().find((chatEvent) => chatEvent.eventType === "RUN_STARTED")?.turnId ?? null;
    const expectedTurnEvents = expectedTurnId
      ? sortedEvents.filter((chatEvent) => chatEvent.turnId === expectedTurnId)
      : [];
    const nextActiveTurnId = expectedTurnEvents.length > 0 ? expectedTurnId : latestStartedTurnId;
    const relevantEvents = nextActiveTurnId
      ? sortedEvents.filter((chatEvent) => chatEvent.turnId === nextActiveTurnId)
      : newEvents;
    const isTerminal = relevantEvents.some((chatEvent) => ["RUN_FINISHED", "RUN_ERROR"].includes(chatEvent.eventType));
    const hasVisibleProgress = relevantEvents.some((chatEvent) =>
      [
        "RUN_STARTED",
        "TEXT_MESSAGE_START",
        "TEXT_MESSAGE_CONTENT",
        "TEXT_MESSAGE_DELTA",
        "REASONING_MESSAGE_CONTENT",
        "TOOL_CALL_START",
        "TOOL_CALL_ARGS",
        "TOOL_CALL_END",
        "TOOL_CALL_RESULT",
        "RUN_FINISHED",
        "RUN_ERROR",
      ].includes(chatEvent.eventType)
    );

    setEvents((current) => mergeEvents(current, sortedEvents));

    if (hasNewEvents) {
      setPendingTurn(null);
      setError((current) => clearSyncWarning(current));
    }

    sequenceRef.current =
      sortedEvents.reduce((max, chatEvent) => Math.max(max, chatEvent.sequence), 0) + 1;

    return { hasNewEvents, isTerminal, activeTurnId: nextActiveTurnId, hasVisibleProgress };
  }, [selectedCustomerId, threadId]);

  useEffect(() => {
    if (transportMode !== "appsync" || activeTurnBaselineSequence === null || !threadId) {
      return;
    }

    let cancelled = false;

    const reconcileLatestTurn = async () => {
      const startedAt = Date.now();
      let lastProgressAt = startedAt;
      let attempt = 0;
      let reconciledTurnId: string | null = activeTurnId;

      while (!cancelled) {
        if (attempt > 0) {
          await wait(POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)]);
        }

        try {
          const result = await refreshThreadEvents(activeTurnBaselineSequence, reconciledTurnId);
          if (cancelled) {
            return;
          }

          if (result.activeTurnId && result.activeTurnId !== reconciledTurnId) {
            reconciledTurnId = result.activeTurnId;
            setActiveTurnId(result.activeTurnId);
          }

          if (result.hasVisibleProgress || result.hasNewEvents) {
            lastProgressAt = Date.now();
          }

          if (result.isTerminal) {
            setError((current) => clearSyncWarning(current));
            setActiveTurnBaselineSequence(null);
            setActiveTurnId(null);
            return;
          }
        } catch (refreshError) {
          if (!cancelled) {
            console.error("Failed to reconcile active Nucleus turn:", refreshError);
            setError(SYNC_REFRESH_FAILURE_MESSAGE);
            setActiveTurnBaselineSequence(null);
            setActiveTurnId(null);
          }
          return;
        }

        const totalElapsedMs = Date.now() - startedAt;
        const idleElapsedMs = Date.now() - lastProgressAt;

        if (totalElapsedMs >= MAX_BACKGROUND_SYNC_TOTAL_MS || idleElapsedMs >= MAX_BACKGROUND_SYNC_IDLE_MS) {
          if (!cancelled) {
            setError(SYNC_TIMEOUT_MESSAGE);
            setActiveTurnBaselineSequence(null);
            setActiveTurnId(null);
          }
          return;
        }

        attempt += 1;
      }
    };

    void reconcileLatestTurn();

    return () => {
      cancelled = true;
    };
  }, [activeTurnBaselineSequence, activeTurnId, refreshThreadEvents, threadId, transportMode]);

  const startTurn = useCallback(
    async (question: string, requestedThinkingEffort: ThinkingEffort) => {
      if (!threadId || !runSettingsJson) {
        return;
      }

      submitLockRef.current = true;
      const pendingCreatedAt = new Date().toISOString();
      const turnId = crypto.randomUUID();
      if (selectedRun?.id) {
        updateActiveChatSession((session) => ({
          ...session,
          title: session.title === DEFAULT_SESSION_TITLE ? buildChatSessionTitle(question) : session.title,
          updatedAt: pendingCreatedAt,
        }));
      }
      setIsSending(true);
      setError(null);
      setPendingTurn({
        id: turnId,
        text: question,
        createdAt: pendingCreatedAt,
        thinkingEffort: requestedThinkingEffort,
      });

      try {
        if (transportMode === "appsync") {
          const baselineSequence = sequenceRef.current - 1;
          setActiveTurnId(turnId);
          setActiveTurnBaselineSequence(baselineSequence);

          const variables: StartNucleusTurnVariables = {
            threadId,
            contextId,
            runSettings: runSettingsJson,
            message: question,
            thinkingEffort: requestedThinkingEffort,
            turnId,
          };

          try {
            await apolloClient.mutate<StartNucleusTurnResult, StartNucleusTurnVariables>({
              mutation: START_NUCLEUS_TURN,
              variables,
            });
          } catch (mutationError) {
            if (!isTurnIdArgumentUnsupported(mutationError)) {
              throw mutationError;
            }

            setActiveTurnId(null);
            await apolloClient.mutate<StartNucleusTurnResult, StartNucleusTurnVariables>({
              mutation: START_NUCLEUS_TURN_LEGACY,
              variables: {
                threadId: variables.threadId,
                contextId: variables.contextId,
                runSettings: variables.runSettings,
                message: variables.message,
                thinkingEffort: variables.thinkingEffort,
              },
            });
          }
        } else {
          await runFakeChatTurn(
            {
              organizationId: selectedCustomerId,
              threadId,
              question,
              runLabel,
              runSettingsJson,
            },
            {
              mode: "local",
              nextSequence: getNextSequence,
              onEventAppended: (appendedEvent) => {
                setEvents((current) => mergeEvent(current, appendedEvent));
              },
            }
          );
          setPendingTurn(null);
        }
      } catch (sendError) {
        const backendMessage = getNucleusBackendMessage(sendError);
        if (backendMessage) {
          setTransportMode("local");
          setModeNotice(backendMessage);
          setError(null);
          setActiveTurnBaselineSequence(null);
          setActiveTurnId(null);

          try {
            await runFakeChatTurn(
              {
                organizationId: selectedCustomerId,
                threadId,
                question,
                runLabel,
                runSettingsJson,
              },
              {
                mode: "local",
                nextSequence: getNextSequence,
                onEventAppended: (appendedEvent) => {
                  setEvents((current) => mergeEvent(current, appendedEvent));
                },
              }
            );
            setPendingTurn(null);
          } catch (fallbackError) {
            setError(getNormalizedErrorMessage(fallbackError) || "Failed to stream local fallback runtime");
          }
        } else {
          setPendingTurn(null);
          setActiveTurnBaselineSequence(null);
          setActiveTurnId(null);
          setError(getNormalizedErrorMessage(sendError) || "Failed to start the backend runtime");
        }
      } finally {
        setIsSending(false);
        submitLockRef.current = false;
      }
    },
    [
      contextId,
      getNextSequence,
      runLabel,
      runSettingsJson,
      selectedCustomerId,
      selectedRun?.id,
      threadId,
      transportMode,
      updateActiveChatSession,
    ]
  );

  useEffect(() => {
    if (!threadId || !runSettingsJson || isBootstrapping || isSending || hasActiveTurn || queuedTurns.length === 0) {
      return;
    }

    const nextQueuedTurn = queuedTurns[0];
    if (!nextQueuedTurn || queueDispatchRef.current === nextQueuedTurn.id) {
      return;
    }

    queueDispatchRef.current = nextQueuedTurn.id;
    setQueuedTurns((current) => current.slice(1));
    void startTurn(nextQueuedTurn.text, nextQueuedTurn.thinkingEffort).finally(() => {
      queueDispatchRef.current = null;
    });
  }, [hasActiveTurn, isBootstrapping, isSending, queuedTurns, runSettingsJson, startTurn, threadId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const question = inputValue.trim();
    if (!question || !threadId || !runSettingsJson) {
      return;
    }

    const submissionSignature = `${threadId}:${thinkingEffort}:${question}`;
    const now = Date.now();
    if (
      lastSubmittedRef.current &&
      lastSubmittedRef.current.signature === submissionSignature &&
      now - lastSubmittedRef.current.submittedAt < SUBMIT_DEDUPE_WINDOW_MS
    ) {
      return;
    }

    lastSubmittedRef.current = { signature: submissionSignature, submittedAt: now };
    setInputValue("");

    if (hasActiveTurn || isSending || submitLockRef.current) {
      const queuedTurn: QueuedTurn = {
        id: `queued-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text: question,
        createdAt: new Date().toISOString(),
        thinkingEffort,
      };
      setQueuedTurns((current) => [...current, queuedTurn]);
      return;
    }

    await startTurn(question, thinkingEffort);
  };

  const handleComposerKeyDown = (keyboardEvent: KeyboardEvent<HTMLTextAreaElement>) => {
    if (keyboardEvent.key === "Enter" && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      if (!selectedRun || isBootstrapping || loadingRuns || inputValue.trim().length === 0) {
        return;
      }
      void handleSubmit(keyboardEvent as unknown as FormEvent);
    }
  };

  return (
    <div css={styles.page()}>
      {modeNotice ? (
        <div css={styles.infoBanner(shellMode)}>
          <AlertCircle size={16} />
          <p css={styles.infoText}>{modeNotice}</p>
        </div>
      ) : null}

      {error ? (
        <div css={styles.errorBanner(shellMode)}>
          <AlertCircle size={16} />
          <p css={styles.errorText}>{error}</p>
        </div>
      ) : null}

      <div css={styles.body(shellMode)}>
        <section css={styles.chatShell(shellMode)}>
          <div css={styles.runToolbar(shellMode)}>
            <div css={styles.runToolbarControls}>
              <div css={styles.runToolbarLeft}>
                <p css={styles.runToolbarLabel}>Current run</p>
                <Select value={selectedRunId ?? ""} onValueChange={setSelectedRunId}>
                  <SelectTrigger css={[styles.runSelector, styles.runTrigger]} disabled={loadingRuns || sortedRuns.length === 0}>
                    <SelectValue placeholder={loadingRuns ? "Loading runs..." : "Select a completed run"} />
                  </SelectTrigger>
                  <SelectContent
                    css={css`
                      max-height: 20rem;
                    `}
                  >
                    {sortedRuns.map((run) => (
                      <SelectItem
                        key={run.id}
                        value={run.id}
                        css={css`
                          font-size: ${theme.typography.fontSize.xs};
                        `}
                      >
                        {formatRunOptionLabel(run)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedRun ? (
                <div css={styles.runToolbarLeft}>
                  <p css={styles.runToolbarLabel}>Chat</p>
                  <Select value={threadId ?? ""} onValueChange={handleChatSessionChange}>
                    <SelectTrigger css={[styles.sessionSelector, styles.runTrigger]} disabled={chatSessions.length === 0}>
                      <SelectValue placeholder="Select a chat session" />
                    </SelectTrigger>
                    <SelectContent
                      css={css`
                        max-height: 20rem;
                      `}
                    >
                      {chatSessions.map((session) => (
                        <SelectItem
                          key={session.threadId}
                          value={session.threadId}
                          css={css`
                            font-size: ${theme.typography.fontSize.xs};
                          `}
                        >
                          {formatChatSessionLabel(session)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    css={styles.toolbarButton}
                    onClick={handleNewChat}
                    disabled={!selectedRun || isBootstrapping || loadingRuns}
                  >
                    <Plus size={14} />
                    New chat
                  </button>
                </div>
              ) : null}
            </div>

            {currentViewLabel ? <p css={styles.runContextText}>Following {currentViewLabel}</p> : null}
          </div>

          {shellMode === "page" ? (
            <div css={styles.brandBar}>
              <NucleusBrand />
            </div>
          ) : null}

          {shelfArtifactItems.length > 0 ? (
            <section css={styles.artifactShelf}>
              <div css={styles.artifactShelfHeader}>
                <p css={styles.artifactShelfTitle}>Files from this chat</p>
                <p css={styles.artifactShelfSubtitle}>Older generated outputs</p>
              </div>
              <div css={styles.generatedFileList}>
                {shelfArtifactItems.map((item) => (
                  <GeneratedFileCard
                    key={`shelf-${item.key}`}
                    artifact={item.artifact}
                    onDownload={(artifact, download) => handleArtifactDownload(item.turnId, artifact, download)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          <section css={[styles.conversationPanel, showLandingState && styles.conversationPanelHero(shellMode)]}>
            <div css={[styles.transcriptBody, showLandingState && styles.transcriptBodyHero]} ref={transcriptRef}>
              {!selectedRun || !runSettingsJson ? (
                <div css={styles.emptyState}>
                  <NucleusEmptyHero
                    mode="select-run"
                    isBootstrapping={isBootstrapping}
                    shellMode={shellMode}
                    greetingName={greetingName}
                  />
                </div>
              ) : turnViewModels.length === 0 && !pendingTurn ? (
                <div css={styles.emptyState}>
                  <NucleusEmptyHero
                    mode="ready"
                    isBootstrapping={isBootstrapping}
                    shellMode={shellMode}
                    greetingName={greetingName}
                  />
                </div>
              ) : (
                <>
                  {hiddenTurns.length > 0 ? (
                    <details css={styles.historyDetails}>
                      <summary>
                        Earlier in this conversation ({hiddenTurns.length} hidden turn{hiddenTurns.length === 1 ? "" : "s"})
                      </summary>
                      <div css={styles.historyList}>
                        {hiddenTurnPreviews.map((turn) => (
                          <div key={turn.turnId} css={styles.historyItem}>
                            <div css={styles.historyItemHeader}>
                              <p css={styles.historyItemText}>{turn.question}</p>
                              <p css={styles.historyItemMeta}>{formatTurnTimestamp(turn.createdAt)}</p>
                            </div>
                            <div css={styles.historyItemAnswer}>
                              {turn.answer ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.answer}</ReactMarkdown>
                              ) : (
                                <p>No answer was captured for this earlier turn.</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : null}

                  {turnViewModels.map((viewModel) => (
                    <div key={viewModel.turn.turnId} css={styles.turnGroup}>
                      {viewModel.turn.userMessage ? (
                        <div css={styles.userRow}>
                          <div css={styles.userBubble}>
                            <p css={styles.userMeta}>You</p>
                            <p css={styles.userText}>{viewModel.turn.userMessage.text}</p>
                          </div>
                        </div>
                      ) : null}

                      <div css={styles.assistantCard(viewModel.statusTone)}>
                        <div css={styles.assistantHeader}>
                          <p css={styles.assistantMeta}>Nucleus</p>
                          <span css={styles.statusChip(viewModel.statusTone)}>
                            {viewModel.statusTone === "working" ? (
                              <Loader2 size={12} css={styles.spinningIcon} />
                            ) : viewModel.statusTone === "complete" ? (
                              <CheckCircle2 size={12} />
                            ) : viewModel.statusTone === "error" ? (
                              <AlertCircle size={12} />
                            ) : (
                              <Activity size={12} />
                            )}
                            {viewModel.statusBadgeLabel}
                          </span>
                        </div>

                        {viewModel.hasError || viewModel.isStale ? (
                          <div css={styles.liveStatus(viewModel.isRunning)}>
                            <div css={styles.liveStatusTextWrap}>
                              <p css={styles.liveStatusEyebrow}>Latest status</p>
                              <p css={styles.liveStatusText}>{viewModel.stageLabel}</p>
                              {viewModel.stageDetail ? <p css={styles.liveStatusDetail}>{viewModel.stageDetail}</p> : null}
                            </div>
                          </div>
                        ) : null}

                        {viewModel.assistantText.trim() ? (
                          viewModel.assistantRenderMode === "markdown" ? (
                            <div css={styles.markdown}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {viewModel.assistantText}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <StreamingAnswerText text={viewModel.assistantText} />
                          )
                        ) : (
                          <div css={styles.placeholderAnswer}>
                            {viewModel.isRunning ? (
                              <Loader2 size={14} css={styles.spinningIcon} />
                            ) : viewModel.isStale ? (
                              <Clock3 size={14} />
                            ) : (
                              <AlertCircle size={14} />
                            )}
                            {viewModel.isRunning
                              ? "Nucleus is working."
                              : viewModel.isStale
                                ? "This earlier attempt stopped updating before the answer finished."
                                : "No answer content arrived for this turn."}
                          </div>
                        )}

                        {viewModel.thinkingBlocks.length > 0 || viewModel.toolTraces.length > 0 ? (
                          viewModel.isRunning || viewModel.hasError ? (
                            <>
                              {viewModel.thinkingBlocks.length > 0 ? (
                                <section css={styles.traceSection}>
                                  <p css={styles.traceSectionLabel}>Thinking</p>
                                  <div css={styles.thinkingList}>
                                    {viewModel.thinkingBlocks.map((block) => (
                                      <div key={block.id} css={styles.thinkingBlock}>
                                        <Sparkles size={14} color={theme.colors.mutedForeground} />
                                        <p css={styles.thinkingText}>{block.text}</p>
                                      </div>
                                    ))}
                                  </div>
                                </section>
                              ) : null}

                              {viewModel.toolTraces.length > 0 ? (
                                <section css={styles.traceSection}>
                                  <p css={styles.traceSectionLabel}>Tools</p>
                                  <div css={styles.toolTraceList}>
                                    {viewModel.toolTraces.map((toolTrace) => (
                                      <div key={toolTrace.id} css={styles.toolTraceRow(toolTrace.state)}>
                                        <div css={styles.toolTraceHeader}>
                                          <div css={styles.toolTraceTitleWrap}>
                                            <WorkIcon state={toolTrace.state} />
                                            <p css={styles.toolTraceTitle}>{toolTrace.title}</p>
                                          </div>
                                          <span css={styles.toolTraceState(toolTrace.state)}>
                                            {toolTrace.state === "working"
                                              ? "Running"
                                              : toolTrace.state === "error"
                                                ? "Error"
                                                : "Done"}
                                          </span>
                                        </div>
                                        {toolTrace.inputDetails || toolTrace.outputDetails ? (
                                          <details css={styles.workInnerDetails}>
                                            <summary>Details</summary>
                                            <pre css={styles.workCode}>
                                              {[toolTrace.inputDetails, toolTrace.outputDetails].filter(Boolean).join("\n\n")}
                                            </pre>
                                          </details>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                </section>
                              ) : null}
                            </>
                          ) : (
                            <details css={styles.workDetails}>
                              <summary>
                                <Wrench size={14} />
                                View work · {getWorkSummaryLabel(viewModel)}
                              </summary>
                              <div css={styles.workList}>
                                {viewModel.thinkingBlocks.length > 0 ? (
                                  <div css={styles.workItem}>
                                    <div css={styles.workIconWrap}>
                                      <Sparkles size={14} />
                                    </div>
                                    <div css={styles.workTextWrap}>
                                      <p css={styles.workTitle}>Thinking</p>
                                      <p css={styles.workSubtitle}>User-facing reasoning notes captured during the turn.</p>
                                      <div css={styles.thinkingList}>
                                        {viewModel.thinkingBlocks.map((block) => (
                                          <div key={block.id} css={styles.thinkingBlock}>
                                            <Sparkles size={14} color={theme.colors.mutedForeground} />
                                            <p css={styles.thinkingText}>{block.text}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                                {viewModel.toolTraces.map((toolTrace) => (
                                  <div key={toolTrace.id} css={styles.workItem}>
                                    <div css={styles.workIconWrap}>
                                      <WorkIcon state={toolTrace.state} />
                                    </div>
                                    <div css={styles.workTextWrap}>
                                      <p css={styles.workTitle}>{toolTrace.title}</p>
                                      <p css={styles.workSubtitle}>
                                        {toolTrace.state === "error"
                                          ? "This tool reported an error."
                                          : toolTrace.state === "working"
                                            ? "This tool is still running."
                                            : "This tool finished successfully."}
                                      </p>
                                      {toolTrace.inputDetails || toolTrace.outputDetails ? (
                                        <details css={styles.workInnerDetails}>
                                          <summary>Details</summary>
                                          <pre css={styles.workCode}>
                                            {[toolTrace.inputDetails, toolTrace.outputDetails].filter(Boolean).join("\n\n")}
                                          </pre>
                                        </details>
                                      ) : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )
                        ) : null}

                        {viewModel.artifacts.length > 0 ? (
                          <div css={styles.artifactList}>
                            {viewModel.artifacts.map((artifact, index) => (
                              <ArtifactCard
                                key={`${viewModel.turn.turnId}-${artifact.type}-${index}`}
                                artifact={artifact}
                                onDownload={(tableArtifact, download) =>
                                  handleArtifactDownload(viewModel.turn.turnId, tableArtifact, download)
                                }
                              />
                            ))}
                          </div>
                        ) : null}

                      </div>
                    </div>
                  ))}

                  {pendingTurn ? <PendingTurnCard pendingTurn={pendingTurn} /> : null}
                </>
              )}
            </div>

            <form css={[styles.composer(shellMode), showLandingState && styles.composerHero]} onSubmit={handleSubmit}>
              {queuedTurns.length > 0 ? (
                <div css={styles.queueList}>
                  {queuedTurns.map((queuedTurn) => (
                    <div key={queuedTurn.id} css={styles.queueChip}>
                      <span css={styles.queueChipText}>Queued next: {queuedTurn.text}</span>
                      <button
                        type="button"
                        css={styles.queueRemoveButton}
                        onClick={() =>
                          setQueuedTurns((current) => current.filter((entry) => entry.id !== queuedTurn.id))
                        }
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              <div css={styles.composerControls}>
                <p css={styles.composerControlLabel}>Thinking effort</p>
                <Select value={thinkingEffort} onValueChange={handleThinkingEffortChange}>
                  <SelectTrigger css={[styles.effortSelector, styles.runTrigger]} disabled={!selectedRun}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {THINKING_EFFORT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div css={styles.composerRow}>
                <textarea
                  css={styles.input}
                  value={inputValue}
                  onChange={(nextEvent) => setInputValue(nextEvent.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder={
                    selectedRun
                      ? "Ask about the selected shipment-planning run..."
                      : "Select a run before sending a question..."
                  }
                  disabled={!selectedRun || isBootstrapping || loadingRuns}
                />
                <button
                  type="submit"
                  css={styles.sendButton(
                    !selectedRun || isBootstrapping || loadingRuns || inputValue.trim().length === 0
                  )}
                  disabled={
                    !selectedRun || isBootstrapping || loadingRuns || inputValue.trim().length === 0
                  }
                >
                  {isSending && !hasActiveTurn ? <Loader2 size={16} css={styles.spinningIcon} /> : <Send size={16} />}
                </button>
              </div>

              {showFooterStatus && footerStatusText ? (
                <p css={styles.footerStatus}>{footerStatusText}</p>
              ) : null}
            </form>
          </section>

          {shellMode === "page" && debugUiEnabled ? (
            <details css={styles.developerDetails}>
              <summary>Developer trace</summary>
              <div css={styles.developerList}>
                {latestTurnEvents.length === 0 ? (
                  <div css={styles.developerItem}>
                    <p css={styles.developerType}>No trace yet</p>
                    <p css={styles.developerText}>Send a prompt to inspect the raw latest-turn event stream.</p>
                  </div>
                ) : (
                  latestTurnEvents.map((activity) => (
                    <div key={activity.eventId} css={styles.developerItem}>
                      <p css={styles.developerType}>{activity.eventType}</p>
                      <p css={styles.developerText}>{describeActivity(activity)}</p>
                      {formatActivityDetails(activity) ? (
                        <details css={styles.developerInnerDetails}>
                          <summary>Details</summary>
                          <pre css={styles.developerCode}>{formatActivityDetails(activity)}</pre>
                        </details>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </details>
          ) : null}
        </section>
      </div>
    </div>
  );
};

async function fetchThreadEvents(threadId: string, organizationId: string) {
  const allEvents: ChatEvent[] = [];
  let nextToken: string | undefined;
  let pageCount = 0;

  do {
    const page = await appSyncService.getChatEventsByThread({
      organizationId,
      threadId,
      limit: 500,
      nextToken: nextToken ?? null,
    });

    if (page.items?.length) {
      allEvents.push(...page.items);
    }

    nextToken = page.nextToken ?? undefined;
    pageCount += 1;
  } while (nextToken && pageCount < 20);

  return allEvents.sort((a, b) => a.sequence - b.sequence);
}
